import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../stores/store';
import { OverviewDto } from '../../models/Pharse2/Overview';
import { ProjectDistinct, LocationWithSourceTypeDto } from '../../models/Pharse2/LocationMap';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { ResourceDataDto } from '../../models/Pharse2/ResourceData';
import LoadingComponent from '../../components/LoadingComponent';
import { getSourceTypeNames, sortLocationListByName } from '../Monitoring/config/monitoringConfig';
import agent from '../../services/Api/agent';
import { LocationDetailDto } from '../../models/LocationDetail';
import { defaultThresholds, getMonitoringStatus } from '../Monitoring/config/thresholds';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const RealtimeGraphPage: React.FC = observer(() => {
  const { overviewStore, userStore, locationMapStore, resourceDataStore, dataStore, graphStore } = useStore();
  const { loadingInitial, getOverviewDtoList } = overviewStore;
  const { getResourceData } = resourceDataStore;
  const { user, CheckIfCastcoAdmin } = userStore;
  const navigate = useNavigate();

  // 檢查權限 - 與 Monitoring 頁面相同
  useEffect(() => {
    if (user && !CheckIfCastcoAdmin() && user.role !== 'Hospital_User') {
      navigate('/dashboard');
    }
  }, [user, CheckIfCastcoAdmin, navigate]);

  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [projectList, setProjectList] = useState<ProjectDistinct[]>([]);
  const [locationList, setLocationList] = useState<LocationWithSourceTypeDto[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedLocationName, setSelectedLocationName] = useState<string>('');
  const [chartData, setChartData] = useState<ResourceDataDto[]>([]);
  const [allLocationsChartData, setAllLocationsChartData] = useState<Map<number, ResourceDataDto[]>>(new Map());
  const [unit, setUnit] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [locationDetail, setLocationDetail] = useState<LocationDetailDto | null>(null);
  const [locationDetailLoading, setLocationDetailLoading] = useState(false);
  const [currentValue, setCurrentValue] = useState<number | undefined>(undefined);
  const [alertThreshold, setAlertThreshold] = useState<number | null | undefined>(undefined);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // 使用 useRef 來保持 alertThreshold Map 的引用，避免每次渲染時重新創建
  const locationAlertThresholdMapRef = useRef<Map<number, number | null | undefined>>(new Map());

  // 獲取項目列表（僅顯示有 vibration 的項目）
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const allProjects = await locationMapStore.loadProjectDistinctList();
        if (!allProjects || allProjects.length === 0) {
          setProjectList([]);
          return;
        }
        const allOverviews = await getOverviewDtoList();
        const vibrationSourceTypeNames = getSourceTypeNames('vibration');
        const projectIdsWithVibration = new Set(
          (allOverviews || [])
            .filter(dto =>
              dto.sourceTypeValues?.some(kv =>
                vibrationSourceTypeNames.some(vst =>
                  (kv.resourceTypeName ?? '').toLowerCase() === vst.toLowerCase()
                )
              )
            )
            .map(dto => dto.projectId)
        );
        const vibrationProjects = allProjects.filter(p => projectIdsWithVibration.has(p.id));
        setProjectList(vibrationProjects);
        if (vibrationProjects.length > 0) {
          setSelectedProjectId(prev => {
            if (prev === null) return vibrationProjects[0].id;
            return vibrationProjects.some(p => p.id === prev) ? prev : vibrationProjects[0].id;
          });
        }
      } catch (error) {
        console.error('Failed to fetch project list:', error);
      }
    };

    fetchProjects();
  }, [locationMapStore, getOverviewDtoList]);

  // 將 OverviewDto 轉換為 LocationWithSourceTypeDto，並保存 alertThreshold
  const convertOverviewDtoToLocationWithSourceType = (overviewDto: OverviewDto): LocationWithSourceTypeDto => {
    // 保存 alertThreshold 到 map 中
    locationAlertThresholdMapRef.current.set(overviewDto.locationId, overviewDto.alertThreshold);

    return {
      locationId: overviewDto.locationId,
      projectId: overviewDto.projectId,
      name: overviewDto.locationName,
      loggersetName: undefined,
      latitude: undefined,
      longtitude: undefined,
      batteryValue: 0,
      locationAlert: overviewDto.alarmValue,
      sourceTypes: overviewDto.sourceTypeValues.map(kv => ({
        id: kv.resourceTypeId,
        sourceType: kv.resourceTypeName,
        unit: kv.unit || undefined,
        status: true,
        access: true,
        remark: undefined
      }))
    };
  };

  // 獲取位置列表
  useEffect(() => {
    if (selectedProjectId === null) {
      setLocationList([]);
      return;
    }

    const fetchLocations = async () => {
      try {
        // 清空 alertThreshold Map，準備填充新數據
        locationAlertThresholdMapRef.current.clear();

        const overviewDtos = await getOverviewDtoList(selectedProjectId);
        if (overviewDtos) {
          const convertedLocations = overviewDtos.map(convertOverviewDtoToLocationWithSourceType);
          // 只顯示有 vibration sourceType 的位置
          const vibrationSourceTypeNames = getSourceTypeNames('vibration');
          const filteredLocations = convertedLocations.filter(loc =>
            loc.sourceTypes.some(st =>
              vibrationSourceTypeNames.some(vst => st.sourceType?.toLowerCase() === vst.toLowerCase())
            )
          );
          // 根據配置的排序順序排序位置列表
          const sortedLocations = sortLocationListByName(filteredLocations);
          setLocationList(sortedLocations);
        } else {
          setLocationList([]);
        }
      } catch (error) {
        console.error('Failed to fetch location list:', error);
        setLocationList([]);
      }
    };

    fetchLocations();
  }, [selectedProjectId, getOverviewDtoList]);

  // 獲取圖表數據
  const fetchChartData = async () => {
    if (!selectedLocationId) {
      setChartData([]);
      setAllLocationsChartData(new Map());
      return;
    }

    setLoading(true);
    try {
      if (selectedLocationId === -1) {
        // 選擇 ALL，獲取所有位置的數據
        const vibrationSourceTypeNames = getSourceTypeNames('vibration');
        const allDataMap = new Map<number, ResourceDataDto[]>();
        const dataPromises: Promise<void>[] = [];

        for (const location of locationList) {
          const vibrationSourceType = location.sourceTypes.find(st =>
            vibrationSourceTypeNames.some(vst => st.sourceType?.toLowerCase() === vst.toLowerCase())
          );

          if (vibrationSourceType) {
            dataPromises.push(
              getResourceData(location.locationId, vibrationSourceType.id, true)
                .then(data => {
                  if (data && Array.isArray(data) && data.length > 0) {
                    allDataMap.set(location.locationId, data);
                  }
                })
                .catch(error => {
                  console.error(`Failed to fetch data for location ${location.locationId}:`, error);
                })
            );
          }
        }

        await Promise.all(dataPromises);
        setAllLocationsChartData(allDataMap);
        setChartData([]);
        // 設置單位（使用第一個位置的單位）
        if (locationList.length > 0) {
          const firstLocation = locationList[0];
          const vibrationSourceType = firstLocation.sourceTypes.find(st =>
            vibrationSourceTypeNames.some(vst => st.sourceType?.toLowerCase() === vst.toLowerCase())
          );
          setUnit(vibrationSourceType?.unit || '');
        }
      } else {
        // 選擇單個位置
        const selectedLocation = locationList.find(loc => loc.locationId === selectedLocationId);
        if (!selectedLocation) {
          setChartData([]);
          setAllLocationsChartData(new Map());
          setLoading(false);
          return;
        }

        const vibrationSourceTypeNames = getSourceTypeNames('vibration');
        const vibrationSourceType = selectedLocation.sourceTypes.find(st =>
          vibrationSourceTypeNames.some(vst => st.sourceType?.toLowerCase() === vst.toLowerCase())
        );

        if (!vibrationSourceType) {
          setChartData([]);
          setAllLocationsChartData(new Map());
          setLoading(false);
          return;
        }

        setUnit(vibrationSourceType.unit || '');

        // 獲取 1 分鐘歷史數據（12 x 5s 數據）
        const data = await getResourceData(selectedLocationId, vibrationSourceType.id, true);
        if (data && Array.isArray(data)) {
          setChartData(data);
          setAllLocationsChartData(new Map());
          // 獲取最新值用於計算 Alarm Level
          if (data.length > 0) {
            const latestData = data[data.length - 1];
            setCurrentValue(latestData.value ?? undefined);
          }
        } else {
          setChartData([]);
          setAllLocationsChartData(new Map());
          setCurrentValue(undefined);
        }
      }
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      setChartData([]);
      setAllLocationsChartData(new Map());
    } finally {
      setLoading(false);
    }
  };

  // 當選擇位置改變時，獲取數據
  useEffect(() => {
    if (selectedLocationId && locationList.length > 0) {
      fetchChartData();
    } else {
      setChartData([]);
      setAllLocationsChartData(new Map());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, locationList]);

  // 設置自動刷新（每 5 秒，與 vibration 相同）
  useEffect(() => {
    if (selectedLocationId && locationList.length > 0) {
      intervalRef.current = setInterval(() => {
        fetchChartData();
      }, 5000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, locationList]);

  // 獲取位置詳情
  const fetchLocationDetail = async (locationId: number) => {
    setLocationDetailLoading(true);
    try {
      const detail = await agent.Locations.getLocationDetail(locationId);
      setLocationDetail(detail);
    } catch (error) {
      console.error('Failed to fetch location detail:', error);
      setLocationDetail(null);
    } finally {
      setLocationDetailLoading(false);
    }
  };

  // 根據監控類型和當前值計算警報級別
  const getAlarmLevelFromThreshold = (): string => {
    if (!currentValue || currentValue === undefined) {
      return locationDetail?.alarmLevel || 'None';
    }

    // 如果提供了 alertThreshold，使用動態閾值；否則使用默認閾值
    if (alertThreshold !== null && alertThreshold !== undefined) {
      // 使用當前圖表的單位
      return `${alertThreshold} ${unit || 'mm/s'}`;
    }

    const config = defaultThresholds.vibration;
    return `${config.max} ${config.unit}`;
  };

  // 處理位置選擇
  const handleLocationChange = (locationId: number | null) => {
    setSelectedLocationId(locationId);
    if (locationId === -1) {
      // 選擇 ALL 時
      setSelectedLocationName('ALL');
      setAlertThreshold(undefined);
      setLocationDetail(null);
      setCurrentValue(undefined);
      return;
    }
    const location = locationList.find(loc => loc.locationId === locationId);
    if (location) {
      setSelectedLocationName(location.name);
      // 獲取該位置的 alertThreshold
      const threshold = locationAlertThresholdMapRef.current.get(location.locationId);
      setAlertThreshold(threshold);
      // 獲取位置詳情
      if (locationId) {
        fetchLocationDetail(locationId);
      } else {
        setLocationDetail(null);
      }
    } else {
      setLocationDetail(null);
      setAlertThreshold(undefined);
    }
  };

  // 顏色列表，用於多個位置的圖表
  const colors = [
    'rgb(59, 130, 246)',   // blue-500
    'rgb(34, 197, 94)',    // green-500
    'rgb(234, 179, 8)',    // yellow-500
    'rgb(239, 68, 68)',    // red-500
    'rgb(168, 85, 247)',   // purple-500
    'rgb(236, 72, 153)',   // pink-500
    'rgb(20, 184, 166)',   // teal-500
    'rgb(251, 146, 60)',   // orange-500
  ];

  // 為單個位置創建圖表配置的函數
  const createChartConfigForLocation = (locationId: number, data: ResourceDataDto[], locationName: string, locationUnit: string) => {
    const sortedData = [...data].sort((a, b) => {
      if (!a.dateTime || !b.dateTime) return 0;
      return new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
    });

    const labels = sortedData.map(d => {
      if (!d.dateTime) return '';
      const date = new Date(d.dateTime);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    });

    // 獲取該位置的 alertThreshold
    const locationAlertThreshold = locationAlertThresholdMapRef.current.get(locationId);
    let max: number | undefined;
    if (locationAlertThreshold !== null && locationAlertThreshold !== undefined) {
      max = locationAlertThreshold;
    } else {
      const thresholdConfig = defaultThresholds.vibration;
      max = thresholdConfig.max;
    }

    const datasets: any[] = [
      {
        label: `Value (${locationUnit})`,
        data: sortedData.map(d => d.value ?? 0),
        borderColor: 'rgb(59, 130, 246)', // blue-500
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        pointRadius: 2,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        pointHoverRadius: 4,
        fill: true,
      },
    ];

    // 添加 Alert 閾值線
    if (max !== undefined) {
      datasets.push({
        label: `Alert (${max} ${locationUnit})`,
        data: labels.map(() => max),
        borderColor: 'rgb(239, 68, 68)', // red-500
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5], // 虛線
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        tension: 0,
      });
    }

    return {
      labels,
      datasets,
      sortedData,
    };
  };

  // 準備圖表數據
  let labels: string[] = [];
  let datasets: any[] = [];

  if (selectedLocationId === -1 && allLocationsChartData.size > 0) {
    // 選擇 ALL，但我們會在渲染時為每個位置創建單獨的圖表
    // 這裡先設置空數據，實際渲染會在下面處理
    labels = [];
    datasets = [];
  } else if (chartData.length > 0) {
    // 選擇單個位置
    const sortedData = [...chartData].sort((a, b) => {
      if (!a.dateTime || !b.dateTime) return 0;
      return new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
    });

    labels = sortedData.map(d => {
      if (!d.dateTime) return '';
      const date = new Date(d.dateTime);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    });

    datasets = [
      {
        label: `Value (${unit})`,
        data: sortedData.map(d => d.value ?? 0),
        borderColor: 'rgb(59, 130, 246)', // blue-500
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverRadius: 6,
        fill: true,
      },
    ];
  }

  // 保存 sortedData 供 tooltip 使用（僅在單個位置時）
  const sortedDataForTooltip = selectedLocationId === -1 ? [] : [...chartData].sort((a, b) => {
    if (!a.dateTime || !b.dateTime) return 0;
    return new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
  });

  // 獲取 vibration 閾值配置
  // 如果提供了 alertThreshold，使用動態閾值；否則使用默認閾值
  let warningMax: number | undefined;
  let max: number | undefined;

  if (alertThreshold !== null && alertThreshold !== undefined) {
    max = alertThreshold; // max = alertThreshold
    warningMax = alertThreshold * 0.8; // warning = alertThreshold * 0.8
  } else {
    const thresholdConfig = defaultThresholds.vibration;
    max = thresholdConfig.max;
  }

  // 添加 Warning 閾值線
  if (warningMax !== undefined) {
    // datasets.push({
    //   label: `Warning (${warningMax} ${unit})`,
    //   data: labels.map(() => warningMax),
    //   borderColor: 'rgb(234, 179, 8)', // yellow-500
    //   backgroundColor: 'transparent',
    //   borderWidth: 2,
    //   borderDash: [5, 5], // 虛線
    //   pointRadius: 0,
    //   pointHoverRadius: 0,
    //   fill: false,
    //   tension: 0,
    // });
  }

  // 添加 Alert 閾值線（如果 max 有值，總是顯示，即使等於 warningMax）
  if (max !== undefined) {
    datasets.push({
      label: `Alert (${max} ${unit})`,
      data: labels.map(() => max),
      borderColor: 'rgb(239, 68, 68)', // red-500
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [5, 5], // 虛線
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false,
      tension: 0,
    });
  }

  const chartDataConfig = {
    labels,
    datasets,
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          color: 'rgba(255, 255, 255, 0.9)',
          font: {
            size: 14,
          },
        },
      },
      title: {
        display: true,
        text: selectedLocationName ? `1 Minute Graph - ${selectedLocationName}` : '1 Minute Graph',
        color: 'rgba(255, 255, 255, 0.9)',
        font: {
          size: 18,
          weight: 'bold' as const,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          title: function(context: any) {
            const index = context[0].dataIndex;
            const timeLabel = context[0].label || '';

            // 如果是選擇 ALL，直接返回時間標籤
            if (selectedLocationId === -1) {
              return timeLabel;
            }

            // 選擇單個位置時，使用詳細日期時間
            const dataPoint = sortedDataForTooltip[index];
            if (dataPoint && dataPoint.dateTime) {
              const date = new Date(dataPoint.dateTime);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              const seconds = String(date.getSeconds()).padStart(2, '0');
              return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
            }
            return timeLabel;
          },
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(3);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Time',
          color: 'rgba(255, 255, 255, 0.9)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 12,
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: `Value (${unit})`,
          color: 'rgba(255, 255, 255, 0.9)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 12,
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  };

  if (loadingInitial && locationList.length === 0) {
    return <LoadingComponent />;
  }

  return (
    <div className="h-screen w-full flex flex-col bg-gray-900 text-white">
      {/* Header */}
      <div className="mb-4 rounded-lg p-4 text-white flex-shrink-0">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold">1 Minute Graph</h1>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Location Detail Info */}
            {selectedLocationId && selectedLocationId !== -1 && (
              <div className="flex items-center gap-4 px-4 py-2 bg-gray-800 rounded-lg border border-gray-700">
                {locationDetailLoading ? (
                  <div className="text-xs text-gray-400">Loading...</div>
                ) : locationDetail ? (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">MAC:</span>
                      <span className="text-sm text-white font-medium">{locationDetail.mac || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Alarm Level:</span>
                      <span className="text-sm text-white font-medium">{getAlarmLevelFromThreshold()}</span>
                    </div>
                  </>
                ) : null}
              </div>
            )}
            <label htmlFor="project-select" className="text-sm font-medium text-gray-300 whitespace-nowrap">
              Select Project:
            </label>
            <select
              id="project-select"
              value={selectedProjectId || ''}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedProjectId(value === '' ? null : parseInt(value));
                setSelectedLocationId(null); // 重置位置選擇
                setSelectedLocationName('');
                setLocationDetail(null); // 重置位置詳情
                setCurrentValue(undefined); // 重置當前值
              }}
              className="px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            >
              {projectList.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>

            <label htmlFor="location-select" className="text-sm font-medium text-gray-300 whitespace-nowrap">
              Select Location:
            </label>
            <select
              id="location-select"
              value={selectedLocationId === -1 ? 'ALL' : (selectedLocationId || '')}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'ALL') {
                  handleLocationChange(-1); // 使用 -1 表示 ALL
                } else {
                  handleLocationChange(value === '' ? null : parseInt(value));
                }
              }}
              className="px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
              disabled={!selectedProjectId || locationList.length === 0}
            >
              <option value="">Select Location</option>
              <option value="ALL">ALL</option>
              {locationList.map((location) => (
                <option key={location.locationId} value={location.locationId}>
                  {location.name}
                </option>
              ))}
            </select>

            {/* Export Buttons */}
            {selectedProjectId && (
              <div className="flex items-center gap-2">
                {selectedLocationId && selectedLocationId !== -1 ? (
                  // 選擇了項目和位置：導出當前位置
                  <>
                    <button
                      onClick={() => {
                        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
                        // 清理位置名稱中的無效字符
                        const cleanLocationName = selectedLocationName.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
                        const filename = `Location_${cleanLocationName}_1hour_${timestamp}.xlsx`;
                        dataStore.exportLocationDataByPeriod(selectedLocationId, '1hour', filename);
                      }}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                      title="Export Current Location 1 Hour Data"
                    >
                      Last 1 Hour
                    </button>
                    <button
                      onClick={() => {
                        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
                        // 清理位置名稱中的無效字符
                        const cleanLocationName = selectedLocationName.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
                        const filename = `Location_${cleanLocationName}_1week_${timestamp}.xlsx`;
                        dataStore.exportLocationDataByPeriod(selectedLocationId, '1week', filename);
                      }}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                      title="Export Current Location 1 Week Data"
                    >
                      Last 1 Week
                    </button>
                    <button
                      onClick={() => {
                        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
                        // 清理位置名稱中的無效字符
                        const cleanLocationName = selectedLocationName.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
                        const filename = `Location_${cleanLocationName}_1month_${timestamp}.xlsx`;
                        dataStore.exportLocationDataByPeriod(selectedLocationId, '1month', filename);
                      }}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                      title="Export Current Location 1 Month Data"
                    >
                      Last 1 Month
                    </button>
                  </>
                ) : (
                  // 只選擇了項目：導出所有位置（合併到一個 Excel）
                  <>
                    <button
                      onClick={() => {
                        if (selectedProjectId) {
                          const selectedProject = projectList.find(p => p.id === selectedProjectId);
                          const projectName = selectedProject?.name || `Project_${selectedProjectId}`;
                          const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
                          // 清理項目名稱中的無效字符
                          const cleanProjectName = projectName.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
                          const filename = `AllLocations_${cleanProjectName}_1hour_${timestamp}.xlsx`;
                          dataStore.exportAllLocationsDataByPeriod(selectedProjectId, '1hour', filename);
                        }
                      }}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                      title="Export All Locations 1 Hour Data"
                    >
                      All Locations 1H
                    </button>
                    <button
                      onClick={() => {
                        if (selectedProjectId) {
                          const selectedProject = projectList.find(p => p.id === selectedProjectId);
                          const projectName = selectedProject?.name || `Project_${selectedProjectId}`;
                          const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
                          // 清理項目名稱中的無效字符
                          const cleanProjectName = projectName.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
                          const filename = `AllLocations_${cleanProjectName}_1week_${timestamp}.xlsx`;
                          dataStore.exportAllLocationsDataByPeriod(selectedProjectId, '1week', filename);
                        }
                      }}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                      title="Export All Locations 1 Week Data (Combined Excel)"
                    >
                      All Locations 1 Week
                    </button>
                    <button
                      onClick={() => {
                        if (selectedProjectId) {
                          const selectedProject = projectList.find(p => p.id === selectedProjectId);
                          const projectName = selectedProject?.name || `Project_${selectedProjectId}`;
                          const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
                          // 清理項目名稱中的無效字符
                          const cleanProjectName = projectName.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
                          const filename = `AllLocations_${cleanProjectName}_1month_${timestamp}.xlsx`;
                          dataStore.exportAllLocationsDataByPeriod(selectedProjectId, '1month', filename);
                        }
                      }}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                      title="Export All Locations 1 Month Data (Combined Excel)"
                    >
                      All Locations 1 Month
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart Area */}
      <div className="flex-1 min-h-0 p-4">
        <div className="bg-gray-800 rounded-lg p-6 h-full">
          {loading && ((selectedLocationId === -1 && allLocationsChartData.size === 0) || (selectedLocationId !== -1 && selectedLocationId !== null && chartData.length === 0)) ? (
            <div className="h-full flex items-center justify-center">
              <LoadingComponent content="Loading chart data..." />
            </div>
          ) : (selectedLocationId === -1 && allLocationsChartData.size === 0) || (selectedLocationId !== -1 && selectedLocationId !== null && chartData.length === 0) ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <p className="text-lg text-gray-400">No data available</p>
                <p className="text-sm mt-2 text-gray-500">
                  {selectedLocationId === -1
                    ? (allLocationsChartData.size === 0 ? 'Loading all locations data...' : 'No data available for any location')
                    : selectedLocationId
                    ? 'Please wait for data to load...'
                    : 'Please select a location to view the graph'}
                </p>
              </div>
            </div>
          ) : selectedLocationId === -1 && allLocationsChartData.size > 0 ? (
            <div className="h-full overflow-y-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 p-2">
                {(() => {
                  // 將 Map 轉換為數組
                  const locationsWithData = Array.from(allLocationsChartData.entries()).map(([locationId, data]) => {
                    const loc = locationList.find(l => l.locationId === locationId);
                    return {
                      locationId,
                      data,
                      location: loc || null,
                    };
                  });

                  // 過濾出有 location 信息的項目
                  const locationsWithInfo = locationsWithData.filter(item => item.location !== null) as Array<{ locationId: number; data: ResourceDataDto[]; location: LocationWithSourceTypeDto }>;

                  // 使用 sortLocationListByName 排序（它需要 { name: string }[] 類型）
                  const sortedLocationNames = sortLocationListByName(
                    locationsWithInfo.map(item => ({ name: item.location.name }))
                  );

                  // 根據排序後的名稱順序重新排列 locationsWithInfo
                  const sortedLocations = sortedLocationNames.map(sortedItem =>
                    locationsWithInfo.find(item => item.location.name === sortedItem.name)!
                  ).filter(item => item !== undefined);

                  // 將沒有 location 信息的項目放在最後
                  const locationsWithoutInfo = locationsWithData.filter(item => item.location === null);

                  return [...sortedLocations, ...locationsWithoutInfo];
                })().map((item) => {
                  const locationId = item.locationId;
                  const data = item.data;
                  const loc = item.location;
                  const locationName = loc?.name || `Location ${locationId}`;
                  const vibrationSourceTypeNames = getSourceTypeNames('vibration');
                  const vibrationSourceType = loc?.sourceTypes.find((st: any) =>
                    vibrationSourceTypeNames.some(vst => st.sourceType?.toLowerCase() === vst.toLowerCase())
                  );
                  const locationUnit = vibrationSourceType?.unit || 'mm/s';

                  const locationChartConfig = createChartConfigForLocation(locationId, data, locationName, locationUnit);

                  // 為每個位置創建單獨的圖表選項
                  const locationChartOptions: any = {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                        position: 'top' as const,
                        labels: {
                          color: 'rgba(255, 255, 255, 0.9)',
                          font: {
                            size: 10,
                          },
                        },
                      },
                      title: {
                        display: true,
                        text: locationName,
                        color: 'rgba(255, 255, 255, 0.9)',
                        font: {
                          size: 18,
                          weight: 'bold' as const,
                        },
                      },
                      tooltip: {
                        enabled: true,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        borderColor: 'rgb(59, 130, 246)',
                        borderWidth: 1,
                        padding: 12,
                        callbacks: {
                          title: function(context: any) {
                            const index = context[0].dataIndex;
                            const dataPoint = locationChartConfig.sortedData[index];
                            if (dataPoint && dataPoint.dateTime) {
                              const date = new Date(dataPoint.dateTime);
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              const hours = String(date.getHours()).padStart(2, '0');
                              const minutes = String(date.getMinutes()).padStart(2, '0');
                              const seconds = String(date.getSeconds()).padStart(2, '0');
                              return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
                            }
                            return context[0].label || '';
                          },
                          label: function(context: any) {
                            let label = context.dataset.label || '';
                            if (label) {
                              label += ': ';
                            }
                            if (context.parsed.y !== null) {
                              label += context.parsed.y.toFixed(3);
                            }
                            return label;
                          }
                        },
                      },
                    },
                    scales: {
                      x: {
                        display: true,
                        title: {
                          display: false,
                          text: 'Time',
                          color: 'rgba(255, 255, 255, 0.9)',
                        },
                        ticks: {
                          color: 'rgba(255, 255, 255, 0.7)',
                          font: {
                            size: 10,
                          },
                          maxRotation: 45,
                          minRotation: 45,
                        },
                        grid: {
                          color: 'rgba(255, 255, 255, 0.1)',
                        },
                      },
                      y: {
                        display: true,
                        title: {
                          display: false,
                          text: `Value (${locationUnit})`,
                          color: 'rgba(255, 255, 255, 0.9)',
                        },
                        ticks: {
                          color: 'rgba(255, 255, 255, 0.7)',
                          font: {
                            size: 10,
                          },
                          maxRotation: 45,
                          minRotation: 45,
                        },
                        grid: {
                          color: 'rgba(255, 255, 255, 0.1)',
                        },
                      },
                    },
                  };

                  return (
                    <div key={locationId} className="bg-gray-800 rounded-lg p-2 h-[280px]">
                      <Line data={locationChartConfig} options={locationChartOptions} />
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="h-full">
              <Line data={chartDataConfig} options={chartOptions} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default RealtimeGraphPage;

